import * as echarts from "echarts";
import { useEffect, useRef } from "react";
import useDarkMode from "use-dark-mode";

interface ChartStats {
  building_total: number;
  ffe_total: number;
  flooded_buildings: number;
  ffe_saved: number;
}

const stats: Record<string, ChartStats> = {
  ffe: {
    building_total: 3854,
    ffe_total: 0,
    flooded_buildings: 0,
    ffe_saved: 0,
  },
  real: {
    building_total: 3854,
    ffe_total: 1898,
    flooded_buildings: 1286,
    ffe_saved: 449,
  },
  "2": {
    building_total: 3854,
    ffe_total: 1898,
    flooded_buildings: 3,
    ffe_saved: 0,
  },
  "5": {
    building_total: 3854,
    ffe_total: 1898,
    flooded_buildings: 847,
    ffe_saved: 389,
  },
  "10": {
    building_total: 3854,
    ffe_total: 1898,
    flooded_buildings: 1673,
    ffe_saved: 559,
  },
  "20": {
    building_total: 3854,
    ffe_total: 1898,
    flooded_buildings: 2224,
    ffe_saved: 587,
  },
  "50": {
    building_total: 3854,
    ffe_total: 1898,
    flooded_buildings: 2720,
    ffe_saved: 510,
  },
  "100": {
    building_total: 3854,
    ffe_total: 1898,
    flooded_buildings: 2920,
    ffe_saved: 468,
  },
  "500": {
    building_total: 3854,
    ffe_total: 1898,
    flooded_buildings: 3491,
    ffe_saved: 89,
  },
  "1500": {
    building_total: 3854,
    ffe_total: 1898,
    flooded_buildings: 3562,
    ffe_saved: 18,
  },
};

interface ChartProps {
  data_key: string;
}

const RadialPieChart = (props: ChartProps) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  const darkMode = useDarkMode(false);
  const isDarkMode = darkMode.value;

  // Initialize chart once
  useEffect(() => {
    if (chartRef.current && !chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  // Update chart when data changes
  useEffect(() => {
    if (!chartInstanceRef.current) return;

    const dataset = stats[props.data_key];

    const options = {
      grid: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        containLabel: false,
      },
      title: {
        left: "center",
        top: "center",
        text: `{title1|${dataset.building_total.toLocaleString()}}\n{title2|Buildings}`,

        textStyle: {
          rich: {
            title1: {
              color: isDarkMode ? "#fff" : "#000",
              fontSize: 24,
            },
            title2: {
              color: isDarkMode ? "#fff" : "#000",
              fontSize: 16,
            },
          },
        },
      },
      tooltip: {
        trigger: "item",
        formatter: function (params: any) {
          return `${params.name}: ${params.value} <br> ${(
            (params.value / dataset.building_total) *
            100
          ).toLocaleString("en-US", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}%`;
        },
      },
      series: [
        {
          type: "pie",
          radius: ["50%", "90%"], // Donut chart style - increased to fill more space
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          label: {
            show: true,
            position: "inside",
            formatter: (params: any) =>
              params.value === 0
                ? ""
                : props.data_key == "ffe"
                ? "Total Buildings"
                : params.name,
            color: isDarkMode ? "#fff" : "black",
            fontWeight: "bold",
            textBorderColor: !isDarkMode ? "white" : "black",
            textBorderWidth: 3,
          },
          labelLine: {
            show: false,
          },
          data: [
            {
              value: dataset.flooded_buildings - dataset.ffe_saved,
              name: "Flooded",
              itemStyle: { color: "#EF476F" },
            },
            {
              value: dataset.ffe_saved,
              name: "FFE Above Flood",
              itemStyle: { color: "#CD64FA" },
            },
            {
              value: dataset.building_total - dataset.flooded_buildings,
              name: "Outside Flood",
              itemStyle: {
                color: props.data_key == "ffe" ? "grey" : "#06D6A0",
              },
            },
          ],
        },
      ],
      backgroundColor: "transparent",
    };

    chartInstanceRef.current.setOption(options, true);
  }, [props.data_key, isDarkMode]);

  return <div ref={chartRef} className="size-[250px] p-2" />;
};

export default RadialPieChart;
